import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, of, timer, Subject} from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';
import { onNotificationClosed, onNotificationTriggered } from '../actions/notification.actions';
import {
    onBlockingRequestEnded,
    onBlockingRequestStarted,
    onResetOverlay,
} from '../actions/request-loading-indicator.actions';
import { DEFAULT_NOTIFICATION_AUTO_HIDE_DELAY } from '../constants/constants';
import { NotificationType } from '../enums/notification-type.enum';
import { NotificationToast } from '../interfaces/NotificationToast.interface';
import { CoreState } from '../interfaces/states/core-state.interface';
import { isBlockingRequestActive, selectAllNotifications } from '../reducers/core-reducers';
import { IAumicoCoreProvider } from './aumico.core.provider.interface';

const SPINNER_OVERLAY_ID = 'aumico-blocking-request-overlay';

@Injectable({
    providedIn: 'root',
})
export class AumicoCoreProvider implements IAumicoCoreProvider, OnDestroy {
    private spinnerSubscription: Subscription;

    constructor(
        public store: Store<CoreState>,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.spinnerSubscription = this.getBlockingRequestIsRunning$()
            .pipe(
                distinctUntilChanged(),
                switchMap((isRunning) =>
                    isRunning ? timer(2000).pipe(map(() => true)) : of(false),
                ),
            )
            .subscribe((showSpinner) => {
                if (showSpinner) {
                    this.showSpinner();
                } else {
                    this.hideSpinner();
                }
            });
    }

    ngOnDestroy(): void {
        this.spinnerSubscription.unsubscribe();
        this.hideSpinner();
    }

    private showSpinner(): void {
        if (this.document.getElementById(SPINNER_OVERLAY_ID)) {
            return;
        }
        const overlay = this.document.createElement('div');
        overlay.id = SPINNER_OVERLAY_ID;
        overlay.style.cssText = [
            'position:fixed',
            'top:0',
            'left:0',
            'width:100%',
            'height:100%',
            'background-color:rgba(0,0,0,0.3)',
            'z-index:9999',
            'display:flex',
            'justify-content:center',
            'align-items:center',
        ].join(';');

        const img = this.document.createElement('img');
        img.src = 'assets/aumico_loader_rings.png';
        img.alt = 'Loading...';
        overlay.appendChild(img);
        this.document.body.appendChild(overlay);
    }

    private hideSpinner(): void {
        const overlay = this.document.getElementById(SPINNER_OVERLAY_ID);
        if (overlay) {
            overlay.remove();
        }
    }

    /** Siehe `getNotificationAction$()`. */
    private readonly notificationAction$ = new Subject<string>();

    dispatchAddNotification(
        titleLocalizationKey: string,
        descriptionLocalizationKey: string,
        type = NotificationType.DEFAULT,
        autoHideDelay?: number, // Make it optional
        /** REV-21374 — optionale Handlung im Toast, z. B. „Rueckgaengig". Additiv. */
        action?: { labelKey: string; id: string; progress?: boolean },
    ): string {
        const notificationToast: NotificationToast = {
            titleLocalizationKey,
            type,
            descriptionLocalizationKey,
            id: uuidV4(),
            autoHideDelay: autoHideDelay ?? DEFAULT_NOTIFICATION_AUTO_HIDE_DELAY, // Use nullish coalescing
            cssClassName: type.toString(),
            ...(action ? { actionLabelKey: action.labelKey, actionId: action.id } : {}),
            ...(action && action.progress ? { actionProgress: true } : {}),
        };
        this.store.dispatch(onNotificationTriggered({ payload: notificationToast }));
        // ⚠️ Die Kennung wandert HERAUS, damit der Aufrufer den Toast auch selbst schliessen
        // kann. Ohne sie blieb ein Toast mit Frist stehen, nachdem die Frist abgelaufen war —
        // mit einem „Rückgängig", das nichts mehr rückgängig macht. Bestehende Aufrufer
        // ignorieren den Rueckgabewert; die Aenderung ist additiv.
        return notificationToast.id;
    }

    /**
     * Wer eine Toast-Handlung ausgeloest hat — die `actionId` des angeklickten Toasts.
     *
     * ⚠️ Ein Subject und kein Store-Slice: das ist ein EREIGNIS, kein Zustand. Im Store abgelegt
     * muesste es nach dem Verarbeiten wieder geloescht werden, und ein vergessenes Loeschen
     * loeste die Handlung beim naechsten Abonnenten erneut aus.
     */
    getNotificationAction$(): Observable<string> {
        return this.notificationAction$.asObservable();
    }

    dispatchNotificationAction(actionId: string): void {
        this.notificationAction$.next(actionId);
    }

    dispatchCloseNotification(identifier: string): void {
        this.store.dispatch(onNotificationClosed({ payload: identifier }));
    }

    getNotifications(): Observable<NotificationToast[]> {
        return this.store.select(selectAllNotifications);
    }

    dispatchBlockingRequest(identifier: string): void {
        this.store.dispatch(onBlockingRequestStarted({ payload: identifier }));
    }

    dispatchBlockingRequestEnd(identifier: string): void {
        this.store.dispatch(onBlockingRequestEnded({ payload: identifier }));
    }
    dispatchResetOverlay() {
        this.store.dispatch(onResetOverlay());
    }
    getBlockingRequestIsRunning$(): Observable<boolean> {
        return this.store.select(isBlockingRequestActive);
    }
}
